import { useState, type FormEvent } from 'react';
import {
  AlertTriangle,
  ArrowLeftRight,
  Boxes,
  ClipboardList,
  Lock,
  PackageX,
  Plus,
  Search,
  SlidersHorizontal,
} from 'lucide-react';

import {
  Badge,
  Button,
  Card,
  CardHeader,
  Checkbox,
  Drawer,
  EmptyState,
  ErrorAlert,
  Field,
  Input,
  PageHeader,
  Select,
  Skeleton,
  StatCard,
  Table,
  Td,
  Textarea,
  Th,
} from '@/components/ui';
import { useProducts } from '@/lib/catalog-api';
import {
  useAdjustStock,
  useInventorySummary,
  useStock,
  useStockMovements,
  useTransferStock,
  useWarehouses,
  type StockRow,
} from '@/lib/inventory-api';
import { errorMessage, fieldErrors, formatDateTime, formatNumber } from '@/lib/utils';

/**
 * Stock levels and the movement ledger (spec §25, §26).
 *
 * Available is `on hand − reserved`, and reserved units belong to orders
 * somebody is waiting on, so the two are always shown separately — a single
 * "stock" number would hide the difference between goods you have and goods you
 * can still sell.
 */
export function InventoryPage() {
  const [warehouseId, setWarehouseId] = useState('');
  const [search, setSearch] = useState('');
  const [lowStock, setLowStock] = useState(false);
  const [outOfStock, setOutOfStock] = useState(false);
  const [page, setPage] = useState(1);
  const [adjusting, setAdjusting] = useState<AdjustTarget | null>(null);
  const [transferring, setTransferring] = useState<StockRow | null>(null);
  const [ledgerFor, setLedgerFor] = useState<StockRow | null>(null);

  const warehouses = useWarehouses();
  const summary = useInventorySummary(warehouseId || undefined);
  const stock = useStock({
    page,
    limit: 20,
    search: search || undefined,
    warehouseId: warehouseId || undefined,
    lowStock: lowStock || undefined,
    outOfStock: outOfStock || undefined,
  });

  const data = stock.data;

  return (
    <>
      <PageHeader
        title="Inventory"
        description="Stock levels per warehouse, with an append-only ledger of every movement"
        action={
          <Button onClick={() => setAdjusting({ kind: 'new', untrackedOnly: false })}>
            <Plus className="size-4" aria-hidden="true" />
            Record stock
          </Button>
        }
      />

      <div className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Available to sell"
          value={formatNumber(summary.data?.totalAvailable ?? 0)}
          icon={<Boxes className="size-5" aria-hidden="true" />}
        />
        <StatCard
          label="Reserved for orders"
          value={formatNumber(summary.data?.totalReserved ?? 0)}
          icon={<Lock className="size-5" aria-hidden="true" />}
          tone="purple"
        />
        <StatCard
          label="Low stock"
          value={formatNumber(summary.data?.lowStockCount ?? 0)}
          icon={<AlertTriangle className="size-5" aria-hidden="true" />}
          tone="warning"
        />
        <StatCard
          label="Out of stock"
          value={formatNumber(summary.data?.outOfStockCount ?? 0)}
          icon={<PackageX className="size-5" aria-hidden="true" />}
          tone="danger"
        />
      </div>

      {summary.data && summary.data.untrackedProducts > 0 && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-warning-500/30 bg-warning-50 px-4 py-2.5 text-xs text-warning-700">
          <span>
            {formatNumber(summary.data.untrackedProducts)} product(s) have no stock record in any
            warehouse. They read as unavailable on the storefront until stock is recorded.
          </span>
          {/* The banner names the problem, so it carries the fix: those products
              have no row, so no row action can reach them. */}
          <Button
            size="sm"
            variant="outline"
            onClick={() => setAdjusting({ kind: 'new', untrackedOnly: true })}
          >
            Record stock
          </Button>
        </div>
      )}

      <Card>
        <div className="flex flex-wrap items-center gap-2 border-b border-line p-4">
          <div className="relative min-w-56 flex-1">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-subtle"
              aria-hidden="true"
            />
            <input
              type="search"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              placeholder="Search by product name or SKU..."
              aria-label="Search stock"
              className="w-full rounded-lg border border-line py-2 pl-9 pr-3 text-sm placeholder:text-ink-subtle focus:border-brand-500 focus:outline-none"
            />
          </div>

          <Select
            aria-label="Filter by warehouse"
            className="w-auto"
            value={warehouseId}
            onChange={(event) => {
              setWarehouseId(event.target.value);
              setPage(1);
            }}
          >
            <option value="">All warehouses</option>
            {(warehouses.data ?? []).map((warehouse) => (
              <option key={warehouse.id} value={warehouse.id}>
                {warehouse.name}
              </option>
            ))}
          </Select>

          <Checkbox
            label="Low stock"
            checked={lowStock}
            onChange={(event) => {
              setLowStock(event.target.checked);
              setPage(1);
            }}
          />
          <Checkbox
            label="Out of stock"
            checked={outOfStock}
            onChange={(event) => {
              setOutOfStock(event.target.checked);
              setPage(1);
            }}
          />
        </div>

        {stock.isPending && (
          <div className="space-y-2 p-4">
            {Array.from({ length: 6 }, (_, index) => (
              <Skeleton key={index} className="h-12 w-full" />
            ))}
          </div>
        )}

        {stock.isError && (
          <div className="p-4">
            <ErrorAlert title="Could not load stock">{errorMessage(stock.error)}</ErrorAlert>
          </div>
        )}

        {data && data.items.length === 0 && (
          <div className="p-4">
            <EmptyState
              title="No stock rows match"
              description="Adjust the filters, or record stock against a product to create its first row."
            />
          </div>
        )}

        {data && data.items.length > 0 && (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Product</Th>
                  <Th>Warehouse</Th>
                  <Th className="text-right">On hand</Th>
                  <Th className="text-right">Reserved</Th>
                  <Th className="text-right">Available</Th>
                  <Th className="text-right">Threshold</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {data.items.map((row) => (
                  <tr key={row.id} className="hover:bg-canvas">
                    <Td>
                      <p className="font-medium text-ink">{row.productName}</p>
                      <p className="text-xs text-ink-subtle">
                        {row.productSku}
                        {row.variantName && ` · ${row.variantName}`}
                      </p>
                    </Td>

                    <Td className="text-ink-muted">
                      {row.warehouseName}
                      <span className="block font-mono text-xs text-ink-subtle">
                        {row.warehouseCode}
                      </span>
                    </Td>

                    <Td className="text-right text-ink">{formatNumber(row.quantity)}</Td>

                    <Td className="text-right text-ink-muted">
                      {row.reservedQuantity > 0 ? formatNumber(row.reservedQuantity) : '—'}
                    </Td>

                    <Td className="text-right">
                      <span
                        className={
                          row.availableQuantity === 0
                            ? 'font-semibold text-danger-600'
                            : row.isLowStock
                              ? 'font-semibold text-warning-700'
                              : 'font-semibold text-ink'
                        }
                      >
                        {formatNumber(row.availableQuantity)}
                      </span>
                      {row.isLowStock && row.availableQuantity > 0 && (
                        <Badge tone="warning" className="ml-1.5">
                          Low
                        </Badge>
                      )}
                    </Td>

                    <Td className="text-right text-ink-subtle">
                      {formatNumber(row.lowStockThreshold)}
                    </Td>

                    <Td>
                      <div className="flex justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => setAdjusting({ kind: 'row', row })}
                          aria-label={`Adjust stock of ${row.productName}`}
                          title="Adjust stock"
                          className="grid size-8 place-items-center rounded-lg text-ink-subtle hover:bg-canvas hover:text-brand-600"
                        >
                          <SlidersHorizontal className="size-4" aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setTransferring(row)}
                          aria-label={`Transfer ${row.productName}`}
                          title="Transfer"
                          className="grid size-8 place-items-center rounded-lg text-ink-subtle hover:bg-canvas hover:text-brand-600"
                        >
                          <ArrowLeftRight className="size-4" aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setLedgerFor(row)}
                          aria-label={`Movement history for ${row.productName}`}
                          title="Movement history"
                          className="grid size-8 place-items-center rounded-lg text-ink-subtle hover:bg-canvas hover:text-brand-600"
                        >
                          <ClipboardList className="size-4" aria-hidden="true" />
                        </button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <div className="flex items-center justify-between border-t border-line px-4 py-3">
              <span className="text-sm text-ink-subtle">
                Page {data.pagination.page} of {data.pagination.totalPages}
                {(lowStock || outOfStock) && ' · filtered within this page'}
              </span>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!data.pagination.hasPreviousPage}
                  onClick={() => setPage((value) => Math.max(1, value - 1))}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!data.pagination.hasNextPage}
                  onClick={() => setPage((value) => value + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>

      <AdjustDrawer target={adjusting} onClose={() => setAdjusting(null)} />
      <TransferDrawer row={transferring} onClose={() => setTransferring(null)} />
      <LedgerDrawer row={ledgerFor} onClose={() => setLedgerFor(null)} />
    </>
  );
}

/**
 * Record a stocktake or a known change.
 *
 * Mounted only while a row is selected, so its state comes from that row's
 * lazy initialisers rather than from an effect syncing props into state.
 */
/**
 * What the adjust drawer is pointed at.
 *
 * `row` adjusts a level that already exists. `new` is the case the row actions
 * cannot express: a product with **no** stock row anywhere has nothing to click
 * on, so the product and warehouse have to be chosen inside the drawer. The API
 * takes a product and a warehouse rather than a row id and creates the row when
 * it is missing, so both cases are the same request.
 */
type AdjustTarget = { kind: 'row'; row: StockRow } | { kind: 'new'; untrackedOnly: boolean };

function AdjustDrawer({ target, onClose }: { target: AdjustTarget | null; onClose: () => void }) {
  if (!target) {
    return null;
  }

  return (
    <AdjustForm
      key={target.kind === 'row' ? target.row.id : 'new'}
      target={target}
      onClose={onClose}
    />
  );
}

function AdjustForm({ target, onClose }: { target: AdjustTarget; onClose: () => void }) {
  const row = target.kind === 'row' ? target.row : null;

  const adjust = useAdjustStock();
  const [mode, setMode] = useState<'set' | 'delta'>('set');
  const [quantity, setQuantity] = useState(() => (row ? String(row.quantity) : ''));
  const [movementType, setMovementType] = useState('ADJUSTMENT');
  const [threshold, setThreshold] = useState(() => (row ? String(row.lowStockThreshold) : ''));
  const [notes, setNotes] = useState('');

  // Only meaningful when there is no row to take them from.
  const [productSearch, setProductSearch] = useState('');
  const [untrackedOnly, setUntrackedOnly] = useState(
    target.kind === 'new' ? target.untrackedOnly : false,
  );
  const [productId, setProductId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');

  const warehouses = useWarehouses();
  const products = useProducts({
    page: 1,
    limit: 20,
    search: productSearch || undefined,
    untracked: untrackedOnly || undefined,
  });

  const errors = fieldErrors(adjust.error);
  const incomplete = !row && (!productId || !warehouseId);

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();

    adjust.mutate(
      {
        productId: row?.productId ?? productId,
        variantId: row?.variantId ?? null,
        warehouseId: row?.warehouseId ?? warehouseId,
        mode,
        quantity: Number(quantity),
        movementType: movementType as 'ADJUSTMENT' | 'PURCHASE' | 'RETURN' | 'DAMAGE',
        lowStockThreshold: threshold.trim() === '' ? undefined : Number(threshold),
        notes: notes.trim() || undefined,
      },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title={row ? 'Adjust stock' : 'Record stock'}
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="adjust-form" disabled={adjust.isPending || incomplete}>
            {adjust.isPending ? 'Saving…' : 'Record movement'}
          </Button>
        </div>
      }
    >
      <form id="adjust-form" onSubmit={handleSubmit} className="space-y-4">
        {adjust.isError && (
          <ErrorAlert title="Could not record this movement">
            {errorMessage(adjust.error)}
          </ErrorAlert>
        )}

        {row ? (
          <div className="rounded-lg bg-canvas px-3 py-2.5 text-sm">
            <p className="font-medium text-ink">{row.productName}</p>
            <p className="text-xs text-ink-subtle">
              {row.productSku} · {row.warehouseName}
            </p>
            <p className="mt-1.5 text-xs text-ink-muted">
              {formatNumber(row.quantity)} on hand · {formatNumber(row.reservedQuantity)} reserved ·{' '}
              {formatNumber(row.availableQuantity)} available
            </p>
          </div>
        ) : (
          <div className="space-y-3 rounded-lg bg-canvas px-3 py-3">
            <Field label="Product" htmlFor="adjust-product" required error={errors['productId']}>
              <Input
                type="search"
                value={productSearch}
                onChange={(event) => setProductSearch(event.target.value)}
                placeholder="Search by name or SKU…"
                aria-label="Search products"
                className="mb-2"
              />
              <Select
                id="adjust-product"
                value={productId}
                onChange={(event) => setProductId(event.target.value)}
                required
              >
                <option value="">
                  {products.isPending ? 'Loading products…' : 'Select a product'}
                </option>
                {(products.data?.items ?? []).map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name} — {product.sku}
                  </option>
                ))}
              </Select>
            </Field>

            <Checkbox
              label="Only products with no stock record"
              checked={untrackedOnly}
              onChange={(event) => {
                setUntrackedOnly(event.target.checked);
                // The current pick may not survive the narrower list.
                setProductId('');
              }}
            />

            {products.data && products.data.items.length === 0 && (
              <p className="text-xs text-ink-subtle">
                No products match.{' '}
                {untrackedOnly && 'Every product may already have a stock row — '}
                try a different search.
              </p>
            )}

            <Field
              label="Warehouse"
              htmlFor="adjust-warehouse"
              required
              error={errors['warehouseId']}
            >
              <Select
                id="adjust-warehouse"
                value={warehouseId}
                onChange={(event) => setWarehouseId(event.target.value)}
                required
              >
                <option value="">Select a warehouse</option>
                {(warehouses.data ?? []).map((warehouse) => (
                  <option key={warehouse.id} value={warehouse.id}>
                    {warehouse.name}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        )}

        <Field label="What are you recording?" htmlFor="adjust-mode">
          <Select
            id="adjust-mode"
            value={mode}
            onChange={(event) => setMode(event.target.value as 'set' | 'delta')}
          >
            <option value="set">A stocktake — I counted this many</option>
            <option value="delta">A change — this many arrived or left</option>
          </Select>
        </Field>

        <Field
          label={mode === 'set' ? 'Counted quantity' : 'Change (negative to remove)'}
          htmlFor="adjust-quantity"
          required
          error={errors['quantity']}
        >
          <Input
            id="adjust-quantity"
            type="number"
            min={mode === 'set' ? 0 : undefined}
            value={quantity}
            onChange={(event) => setQuantity(event.target.value)}
            required
          />
        </Field>

        <Field
          label="Reason"
          htmlFor="adjust-type"
          hint="Recorded in the ledger so the level stays explainable."
        >
          <Select
            id="adjust-type"
            value={movementType}
            onChange={(event) => setMovementType(event.target.value)}
          >
            <option value="ADJUSTMENT">Adjustment / stocktake</option>
            <option value="PURCHASE">Goods received</option>
            <option value="RETURN">Customer return</option>
            <option value="DAMAGE">Damaged or written off</option>
          </Select>
        </Field>

        <Field label="Low-stock threshold" htmlFor="adjust-threshold">
          <Input
            id="adjust-threshold"
            type="number"
            min={0}
            value={threshold}
            onChange={(event) => setThreshold(event.target.value)}
          />
        </Field>

        <Field label="Notes" htmlFor="adjust-notes">
          <Textarea
            id="adjust-notes"
            rows={2}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder="Quarterly count, damaged in transit…"
          />
        </Field>
      </form>
    </Drawer>
  );
}

function TransferDrawer({ row, onClose }: { row: StockRow | null; onClose: () => void }) {
  if (!row) {
    return null;
  }

  return <TransferForm key={row.id} row={row} onClose={onClose} />;
}

function TransferForm({ row, onClose }: { row: StockRow; onClose: () => void }) {
  const warehouses = useWarehouses();
  const transfer = useTransferStock();
  const [toWarehouseId, setToWarehouseId] = useState('');
  const [quantity, setQuantity] = useState('');
  const [notes, setNotes] = useState('');

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();

    transfer.mutate(
      {
        productId: row.productId,
        variantId: row.variantId,
        fromWarehouseId: row.warehouseId,
        toWarehouseId,
        quantity: Number(quantity),
        notes: notes.trim() || undefined,
      },
      { onSuccess: () => onClose() },
    );
  };

  const destinations = (warehouses.data ?? []).filter(
    (warehouse) => warehouse.id !== row.warehouseId && warehouse.isActive,
  );

  return (
    <Drawer
      open
      onClose={onClose}
      title="Transfer stock"
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="transfer-form" disabled={transfer.isPending}>
            {transfer.isPending ? 'Transferring…' : 'Transfer'}
          </Button>
        </div>
      }
    >
      <form id="transfer-form" onSubmit={handleSubmit} className="space-y-4">
        {transfer.isError && (
          <ErrorAlert title="Could not transfer">{errorMessage(transfer.error)}</ErrorAlert>
        )}

        <div className="rounded-lg bg-canvas px-3 py-2.5 text-sm">
          <p className="font-medium text-ink">{row.productName}</p>
          <p className="text-xs text-ink-subtle">
            From {row.warehouseName} · {formatNumber(row.availableQuantity)} available
          </p>
        </div>

        <Field label="Destination warehouse" htmlFor="transfer-to" required>
          <Select
            id="transfer-to"
            value={toWarehouseId}
            onChange={(event) => setToWarehouseId(event.target.value)}
            required
          >
            <option value="">Select a warehouse…</option>
            {destinations.map((warehouse) => (
              <option key={warehouse.id} value={warehouse.id}>
                {warehouse.name}
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label="Quantity"
          htmlFor="transfer-quantity"
          required
          hint="Reserved units stay put — only unreserved stock can move."
        >
          <Input
            id="transfer-quantity"
            type="number"
            min={1}
            max={row.availableQuantity}
            value={quantity}
            onChange={(event) => setQuantity(event.target.value)}
            required
          />
        </Field>

        <Field label="Notes" htmlFor="transfer-notes">
          <Textarea
            id="transfer-notes"
            rows={2}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </Field>
      </form>
    </Drawer>
  );
}

/** The append-only ledger for one product. */
function LedgerDrawer({ row, onClose }: { row: StockRow | null; onClose: () => void }) {
  const movements = useStockMovements({ productId: row?.productId, limit: 50 });

  return (
    <Drawer open={row !== null} onClose={onClose} title="Movement history">
      {row && (
        <div className="space-y-3">
          <CardHeader title={row.productName} className="border-0 px-0 py-0" />

          {movements.isPending && <Skeleton className="h-40 w-full" />}

          {movements.data?.items.length === 0 && (
            <EmptyState title="No movements yet" description="Stock changes will appear here." />
          )}

          <ul className="space-y-2">
            {(movements.data?.items ?? []).map((movement) => (
              <li key={movement.id} className="rounded-lg border border-line px-3 py-2.5">
                <div className="flex items-center justify-between gap-2">
                  <Badge tone={movement.quantity >= 0 ? 'success' : 'danger'}>
                    {movement.type}
                  </Badge>
                  <span className="text-xs text-ink-subtle">
                    {formatDateTime(movement.createdAt)}
                  </span>
                </div>

                <p className="mt-1.5 text-sm text-ink">
                  {movement.quantity > 0 ? '+' : ''}
                  {formatNumber(movement.quantity)} · {formatNumber(movement.quantityBefore)} →{' '}
                  {formatNumber(movement.quantityAfter)}
                </p>

                <p className="text-xs text-ink-subtle">
                  {movement.warehouseName}
                  {movement.performedBy && ` · ${movement.performedBy}`}
                </p>

                {movement.notes && <p className="mt-1 text-xs text-ink-muted">{movement.notes}</p>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Drawer>
  );
}
